import React, { useState, useEffect, useRef, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useOutletContext } from 'react-router-dom'
import axios from 'axios'
import toastr from 'toastr'
import AddTravelModal from '../../components/Modals/leaveinfos/Addtravelmodal'

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL

// ─── Config ───────────────────────────────────────────────────────────────────
const statusConfig = {
  Approved:      { color: '#15803d', bg: '#f0fdf4', border: '#86efac' },
  Declined:      { color: '#b91c1c', bg: '#fef2f2', border: '#fca5a5' },
  'On Progress': { color: '#b45309', bg: '#fffbeb', border: '#fcd34d' },
  Pending:       { color: '#1d4ed8', bg: '#eff6ff', border: '#93c5fd' },
  Cancelled:     { color: '#6b7280', bg: '#f3f4f6', border: '#d1d5db' },
  Completed:     { color: '#065f46', bg: '#ecfdf5', border: '#6ee7b7' },
}

const wayColor = {
  'One Way':    { color: '#0c447c', bg: '#e6f1fb' },
  'Multicity':  { color: '#085041', bg: '#d1fae5' },
  'Round Trip': { color: '#5b21b6', bg: '#ede9fe' },
}

// ─── Helper — is the trip date already past today? ────────────────────────────
const isTravelCompleted = (dateTo) => {
  if (!dateTo) return false
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const end = new Date(dateTo)
  end.setHours(0, 0, 0, 0)
  return end < today
}

// ─── Can this row be cancelled? ───────────────────────────────────────────────
// Rules (mirrored on backend too):
//   1. status must NOT already be Cancelled or Completed
//   2. date_to must NOT be in the past
const canCancel = (t) => {
  if (t.status === 'Cancelled' || t.status === 'Completed') return false
  if (isTravelCompleted(t.date_to)) return false
  return true
}

// ─── Animations ───────────────────────────────────────────────────────────────
const containerVariants = { hidden: {}, visible: { transition: { staggerChildren: 0.08 } } }
const cardVariants = {
  hidden:  { opacity: 0, y: 32, scale: 0.96 },
  visible: { opacity: 1, y: 0, scale: 1, transition: { type: 'spring', stiffness: 200, damping: 22 } },
}

// ─── Stat Card ────────────────────────────────────────────────────────────────
function StatCard({ card, index }) {
  const [hovered, setHovered] = useState(false)
  return (
    <motion.div
      variants={cardVariants}
      onHoverStart={() => setHovered(true)}
      onHoverEnd={() => setHovered(false)}
      whileHover={{ y: -6, scale: 1.02 }}
      style={{
        background: card.bg,
        borderRadius: 18,
        padding: '20px 20px 16px',
        position: 'relative',
        overflow: 'hidden',
        cursor: 'default',
        border: '1px solid rgba(255,255,255,0.08)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        minHeight: 148,
        boxShadow: hovered
          ? `0 12px 40px rgba(0,0,0,0.4), 0 0 0 1px ${card.accent}44`
          : '0 4px 20px rgba(0,0,0,0.25)',
        transition: 'box-shadow 0.3s',
      }}
    >
      <motion.div
        animate={{ scale: hovered ? 1.6 : 1, opacity: hovered ? 0.15 : 0.08 }}
        transition={{ duration: 0.4 }}
        style={{
          position: 'absolute', top: -20, right: -20,
          fontSize: 80, pointerEvents: 'none', filter: 'blur(2px)',
        }}
      >
        {card.icon}
      </motion.div>

      <div style={{ position: 'relative', zIndex: 1 }}>
        <p style={{ color: 'rgba(255,255,255,0.6)', margin: '0 0 6px', fontSize: 12, fontWeight: 500, lineHeight: 1.3 }}>
          {card.label}
        </p>
        <motion.h3
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: index * 0.08 + 0.3, type: 'spring', stiffness: 300 }}
          style={{ color: '#fff', margin: 0, fontSize: 34, fontWeight: 800, lineHeight: 1 }}
        >
          {String(card.value).padStart(2, '0')}
        </motion.h3>
        <p style={{ color: 'rgba(255,255,255,0.35)', margin: '4px 0 0', fontSize: 11 }}>
          {card.unit || 'trips'}
        </p>
      </div>

      {card.subLabel && (
        <div style={{
          position: 'relative', zIndex: 1, marginTop: 14,
          padding: '6px 10px', borderRadius: 8,
          background: 'rgba(255,255,255,0.07)',
          border: '1px solid rgba(255,255,255,0.10)',
          fontSize: 11, color: 'rgba(255,255,255,0.50)', fontWeight: 500,
          display: 'flex', alignItems: 'center', gap: 5, overflow: 'hidden',
        }}>
          <span style={{
            display: 'inline-block', width: 5, height: 5, borderRadius: '50%',
            background: card.accent, flexShrink: 0, opacity: 0.8,
          }} />
          <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {card.subLabel}
          </span>
        </div>
      )}
    </motion.div>
  )
}

// ─── Status Badge ─────────────────────────────────────────────────────────────
function StatusBadge({ status }) {
  const cfg = statusConfig[status] || statusConfig['On Progress']
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '4px 10px', borderRadius: 20,
      border: `1.5px solid ${cfg.border}`,
      background: cfg.bg, color: cfg.color,
      fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap',
    }}>
      <span style={{
        width: 6, height: 6, borderRadius: '50%', background: cfg.color,
        display: 'inline-block',
        animation: status === 'On Progress' || status === 'Pending'
          ? 'trvBlink 1.8s infinite' : 'none',
      }} />
      {status}
    </span>
  )
}

// ─── Route Cell ───────────────────────────────────────────────────────────────
function RouteCell({ from, to }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'nowrap' }}>
      <span style={{
        padding: '3px 8px', borderRadius: 6,
        background: '#e6f1fb', color: '#0c447c', fontSize: 12, fontWeight: 700,
      }}>
        {from || '—'}
      </span>
      <span style={{ fontSize: 13, color: '#9ca3af', flexShrink: 0 }}>✈</span>
      <span style={{
        padding: '3px 8px', borderRadius: 6,
        background: '#d1fae5', color: '#085041', fontSize: 12, fontWeight: 700,
      }}>
        {to || '—'}
      </span>
    </div>
  )
}

// ─── Legs Tooltip — smart positioning so it never overflows the viewport ──────
function LegsTooltip({ legs }) {
  const [show, setShow]   = useState(false)
  const [pos,  setPos]    = useState({ top: 'auto', bottom: 'auto', left: '50%' })
  const triggerRef        = useRef(null)
  const tooltipRef        = useRef(null)

  const recalc = useCallback(() => {
    if (!triggerRef.current) return
    const tr  = triggerRef.current.getBoundingClientRect()
    const vw  = window.innerWidth
    const TH  = 160
    const TW  = 220

    let topVal = 'auto', bottomVal = 'auto'
    if (tr.top > TH + 16) {
      bottomVal = '130%'
    } else {
      topVal = '130%'
    }

    let leftVal = '50%'
    let transform = 'translateX(-50%)'
    const trigCentreX = tr.left + tr.width / 2
    if (trigCentreX - TW / 2 < 8) {
      leftVal = '0'; transform = 'translateX(0)'
    } else if (trigCentreX + TW / 2 > vw - 8) {
      leftVal = 'auto'; transform = 'translateX(0)'
    }

    setPos({ top: topVal, bottom: bottomVal, left: leftVal, transform })
  }, [])

  const open  = () => { recalc(); setShow(true)  }
  const close = () => { setShow(false) }

  if (!legs || legs.length <= 1) return null

  return (
    <div ref={triggerRef} style={{ position: 'relative', display: 'inline-flex', marginTop: 4 }}>
      <span
        onMouseEnter={open}
        onMouseLeave={close}
        onFocus={open}
        onBlur={close}
        tabIndex={0}
        style={{
          cursor: 'pointer', color: '#378add', fontSize: 11,
          fontWeight: 600, background: '#e6f1fb', padding: '2px 7px',
          borderRadius: 99, userSelect: 'none',
        }}
      >
        +{legs.length - 1} more leg{legs.length - 1 > 1 ? 's' : ''}
      </span>

      <AnimatePresence>
        {show && (
          <motion.div
            ref={tooltipRef}
            initial={{ opacity: 0, scale: 0.92, y: 4 }}
            animate={{ opacity: 1, scale: 1,    y: 0 }}
            exit={{   opacity: 0, scale: 0.92, y: 4 }}
            transition={{ duration: 0.14 }}
            style={{
              position:  'absolute',
              top:       pos.top,
              bottom:    pos.bottom,
              left:      pos.left,
              right:     pos.left === 'auto' ? 0 : 'auto',
              transform: pos.transform,
              zIndex:    300,
              background:'#1e293b',
              border:    '1px solid #334155',
              borderRadius: 10,
              padding:   '10px 14px',
              fontSize:  12,
              color:     '#e2e8f0',
              whiteSpace:'nowrap',
              boxShadow: '0 8px 32px rgba(0,0,0,0.35)',
              minWidth:  190,
              pointerEvents: 'none',
            }}
          >
            <div style={{
              position: 'absolute',
              [pos.bottom !== 'auto' ? 'bottom' : 'top']: -5,
              left: '50%', transform: 'translateX(-50%)',
              width: 8, height: 8,
              background: '#1e293b',
              border: '1px solid #334155',
              borderRight: 'none', borderBottom: 'none',
              rotate: pos.bottom !== 'auto' ? '-45deg' : '225deg',
            }} />
            <p style={{ margin: '0 0 8px', fontSize: 10, fontWeight: 700, color: '#60a5fa', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
              All legs
            </p>
            {legs.map((l, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: i < legs.length - 1 ? 6 : 0 }}>
                <span style={{
                  width: 16, height: 16, borderRadius: '50%', flexShrink: 0,
                  background: '#334155', display: 'flex', alignItems: 'center',
                  justifyContent: 'center', fontSize: 9, fontWeight: 700, color: '#93c5fd',
                }}>
                  {i + 1}
                </span>
                <span style={{ color: '#cbd5e1' }}>
                  {l.city_from || '—'}
                  <span style={{ color: '#60a5fa', margin: '0 4px' }}>→</span>
                  {l.city || '—'}
                </span>
                {l.departure_date && (
                  <span style={{ color: '#64748b', fontSize: 11, marginLeft: 2 }}>
                    {new Date(l.departure_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                  </span>
                )}
              </div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

// ─── Cancel Confirmation Dialog ───────────────────────────────────────────────
function CancelDialog({ travel, onConfirm, onClose }) {
  const fmt = (d) => {
    if (!d) return '—'
    return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 600,
        background: 'rgba(15,23,42,0.5)', backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 20, boxSizing: 'border-box',
      }}
    >
      <motion.div
        initial={{ scale: 0.88, y: 24, opacity: 0 }}
        animate={{ scale: 1,    y: 0,  opacity: 1 }}
        exit={{   scale: 0.88, y: 24, opacity: 0 }}
        transition={{ type: 'spring', stiffness: 260, damping: 24 }}
        onClick={e => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: 16,
          width: '100%', maxWidth: 420,
          border: '1px solid #e5e7eb', overflow: 'hidden',
          boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
        }}
      >
        {/* Red accent bar */}
        <div style={{ height: 4, background: 'linear-gradient(90deg, #ef4444, #f97316)' }} />

        <div style={{ padding: '24px 26px 20px' }}>
          {/* Icon + title */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
            <div style={{
              width: 40, height: 40, borderRadius: 10, flexShrink: 0,
              background: '#fef2f2', border: '1.5px solid #fca5a5',
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18,
            }}>
              🚫
            </div>
            <div>
              <p style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#0c1f3a' }}>
                Cancel Travel Request
              </p>
              <p style={{ margin: 0, fontSize: 12, color: '#9ca3af' }}>
                This action cannot be undone
              </p>
            </div>
          </div>

          {/* Trip summary */}
          <div style={{
            background: '#f8fafc', border: '1px solid #e5e7eb',
            borderRadius: 10, padding: '12px 14px', marginBottom: 16,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <span style={{ padding: '2px 8px', borderRadius: 6, background: '#e6f1fb', color: '#0c447c', fontSize: 12, fontWeight: 700 }}>
                {travel.from || '—'}
              </span>
              <span style={{ color: '#9ca3af', fontSize: 13 }}>✈</span>
              <span style={{ padding: '2px 8px', borderRadius: 6, background: '#d1fae5', color: '#085041', fontSize: 12, fontWeight: 700 }}>
                {travel.to || '—'}
              </span>
            </div>
            <p style={{ margin: 0, fontSize: 12, color: '#6b7280' }}>
              {fmt(travel.date_from)}
              {travel.date_to && travel.date_to !== travel.date_from && (
                <> → {fmt(travel.date_to)}</>
              )}
            </p>
          </div>

          <p style={{ margin: '0 0 20px', fontSize: 13, color: '#374151', lineHeight: 1.6 }}>
            Are you sure you want to cancel this travel request? The status will be updated to{' '}
            <span style={{ fontWeight: 700, color: '#b91c1c' }}>Cancelled</span>. The travel desk will be notified.
          </p>

          {/* Buttons */}
          <div style={{ display: 'flex', gap: 10 }}>
            <button
              onClick={onClose}
              style={{
                flex: 1, padding: '10px', borderRadius: 9,
                border: '1.5px solid #e5e7eb', background: '#fff',
                fontWeight: 600, cursor: 'pointer',
                fontFamily: 'inherit', fontSize: 14, color: '#6b7280',
              }}
            >
              Keep it
            </button>
            <button
              onClick={onConfirm}
              style={{
                flex: 1, padding: '10px', borderRadius: 9,
                border: 'none',
                background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                color: '#fff', fontWeight: 700, cursor: 'pointer',
                fontFamily: 'inherit', fontSize: 14,
              }}
            >
              Yes, cancel trip
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  )
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function TeamTravelView({ viewUserId = null }) {
  const { token, setGlobalLoading } = useOutletContext()

  const [travels,      setTravels]      = useState([])
  const [stats,        setStats]        = useState({})
  const [search,       setSearch]       = useState('')
  const [filterStatus, setFilterStatus] = useState('All')
  const [filterWay,    setFilterWay]    = useState('All')
  const [cancelTarget, setCancelTarget] = useState(null) // travel row awaiting confirmation

const fetchTravels = useCallback(async () => {
  setGlobalLoading(true);

  try {
    const params = {};
    if (viewUserId !== null && viewUserId !== undefined && viewUserId !== "") {
      params.userId = Number(viewUserId);
    }

    const res = await axios.get(
      `${API_BASE_URL}/employeeinfos/travel/get_travel_details`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params,
      }
    );

    if (res.status === 200) {
      setTravels(res.data.data || []);
      setStats(res.data.stats || {});
    }
  } catch (err) {
    toastr.error(
      err?.response?.data?.message || "Failed to fetch travel history"
    );
  } finally {
    setGlobalLoading(false);
  }
}, [token, viewUserId, setGlobalLoading]);

useEffect(() => {
  fetchTravels();
}, [fetchTravels]);

  // ── Cancel — API call after confirmation ──────────────────────────────────
  const handleCancelConfirm = async () => {
    if (!cancelTarget) return
    const target = cancelTarget
    setCancelTarget(null) // close dialog immediately
    setGlobalLoading(true)
    try {
      const res = await axios.patch(
        `${API_BASE_URL}/employeeinfos/travel/cancel/${target.id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      )
      if (res.status === 200) {
        toastr.success(res.data.message || 'Travel cancelled successfully')
        // Optimistic update — no extra fetch needed
        setTravels(prev =>
          prev.map(t => t.id === target.id ? { ...t, status: 'Cancelled' } : t)
        )
      }
    } catch (err) {
      toastr.error(err?.response?.data?.message || 'Failed to cancel travel')
    } finally {
      setGlobalLoading(false)
    }
  }

  // ── Stat cards ────────────────────────────────────────────────────────────
  const statCards = [
    {
      label:    'Total Trips',
      value:    stats.total || 0,
      subLabel: `${stats.approved || 0} approved · ${stats.pending || 0} pending`,
      icon:     '✈️',
      accent:   '#7c3aed',
      bg:       'linear-gradient(135deg, #2d1b69 0%, #1a1a2e 100%)',
    },
    {
      label:    'Upcoming Trips',
      value:    stats.upcoming || 0,
      subLabel: 'approved & scheduled',
      unit:     'ahead',
      icon:     '📅',
      accent:   '#0ea5e9',
      bg:       'linear-gradient(135deg, #0f3460 0%, #16213e 100%)',
    },
    {
      label:    'Approved',
      value:    stats.approved || 0,
      subLabel: `of ${stats.total || 0} total requests`,
      icon:     '✅',
      accent:   '#10b981',
      bg:       'linear-gradient(135deg, #064e3b 0%, #1a1a2e 100%)',
    },
    {
      label:    `Days Travelled (${new Date().getFullYear()})`,
      value:    stats.totalDaysThisYear || 0,
      subLabel: 'across all approved trips this year',
      unit:     'days',
      icon:     '📈',
      accent:   '#f59e0b',
      bg:       'linear-gradient(135deg, #451a03 0%, #1a1a2e 100%)',
    },
  ]

  // ── Filtering ─────────────────────────────────────────────────────────────
  const filtered = travels.filter(t => {
    const q = search.toLowerCase()
    const matchSearch =
      t.from?.toLowerCase().includes(q)            ||
      t.to?.toLowerCase().includes(q)              ||
      t.approver?.name?.toLowerCase().includes(q)  ||
      t.way?.toLowerCase().includes(q)             ||
      t.country?.toLowerCase().includes(q)
    const matchStatus = filterStatus === 'All' || t.status === filterStatus
    const matchWay    = filterWay    === 'All' || t.way    === filterWay
    return matchSearch && matchStatus && matchWay
  })

  // Added Cancelled + Completed to filter dropdown
  const statusTypes = ['All', 'Approved', 'On Progress', 'Declined', 'Cancelled', 'Completed']
  const wayTypes    = ['All', 'One Way', 'Multicity']

  const fmt = (d) => {
    if (!d) return '—'
    return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
  }

  return (
    <div
      className="tv-page"
      style={{
        minHeight: '100vh',
        background: '#f8fafc',
        color: '#111827',
        fontFamily: '"DM Sans", "Segoe UI", sans-serif',
        padding: '28px 32px',
      }}
    >
      <style>{`
        @keyframes trvBlink {
          0%,100% { opacity:1; transform:scale(1) }
          50%      { opacity:.35; transform:scale(1.6) }
        }
        .tv-row { transition: background 0.12s; }
        .tv-row:hover { background: #f8fafc !important; }
        .tv-fi:focus { border-color: #1d6fb8 !important; outline: none; }
        .tv-cancel-btn:hover {
          background: #fef2f2 !important;
          color: #b91c1c !important;
          border-color: #fca5a5 !important;
        }

        /* ── Grid ── */
        .tv-cards-grid { grid-template-columns: repeat(4, 1fr); }

        /* ── Responsive ── */
        @media (max-width: 1024px) {
          .tv-cards-grid { grid-template-columns: repeat(2, 1fr) !important; }
        }
        @media (max-width: 640px) {
          .tv-page    { padding: 16px 14px !important; }
          .tv-cards-grid { grid-template-columns: repeat(2, 1fr) !important; gap: 10px !important; }
          .tv-hide-sm { display: none !important; }
          .tv-toolbar { flex-direction: column !important; align-items: stretch !important; }
          .tv-toolbar-filters { flex-direction: column !important; width: 100% !important; }
          .tv-toolbar-filters input,
          .tv-toolbar-filters select { width: 100% !important; box-sizing: border-box; }
          .tv-header  { flex-direction: column !important; align-items: flex-start !important; gap: 12px !important; }
          .tv-header button { width: 100% !important; }
        }
      `}</style>

      {/* ── Header ───────────────────────────────────────────────────────── */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45 }}
        className="tv-header"
        style={{
          display: 'flex', alignItems: 'center',
          justifyContent: 'space-between', marginBottom: 28,
          flexWrap: 'wrap', gap: 12,
        }}
      >
        <h2 style={{ margin: 0, fontSize: 26, fontWeight: 800, letterSpacing: '-0.5px', color: '#111827' }}>
          Travel History
        </h2>
      </motion.div>

      {/* ── Stat Cards ───────────────────────────────────────────────────── */}
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="tv-cards-grid"
        style={{ display: 'grid', gap: 16, marginBottom: 20 }}
      >
        {statCards.map((card, i) => (
          <StatCard key={card.label} card={card} index={i} />
        ))}
      </motion.div>

      {/* ── Table Card ───────────────────────────────────────────────────── */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.35, duration: 0.45 }}
        style={{
          background: '#ffffff', borderRadius: 16,
          border: '1px solid #e2e8f0',
          boxShadow: '0 2px 16px rgba(0,0,0,0.07)',
        }}
      >
        {/* Toolbar */}
        <div
          className="tv-toolbar"
          style={{
            padding: '16px 24px', borderBottom: '1px solid #f1f5f9',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            flexWrap: 'wrap', gap: 12,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>Travel Requests</span>
            <span style={{
              padding: '3px 10px', borderRadius: 99,
              background: '#e6f1fb', color: '#0c447c',
              fontSize: 12, fontWeight: 600, border: '1px solid #b5d4f4',
            }}>
              Total: {travels.length}
            </span>
            <span style={{
              padding: '3px 10px', borderRadius: 99,
              background: '#f1f5f9', color: '#64748b', fontSize: 12, fontWeight: 600,
            }}>
              Showing: {filtered.length}
            </span>
          </div>

          <div
            className="tv-toolbar-filters"
            style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}
          >
            <div style={{ position: 'relative', flex: 1, minWidth: 160 }}>
              <span style={{
                position: 'absolute', left: 10, top: '50%',
                transform: 'translateY(-50%)', fontSize: 14, color: '#94a3b8',
              }}>🔍</span>
              <input
                className="tv-fi"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search city, approver..."
                style={{
                  width: '100%', boxSizing: 'border-box',
                  padding: '7px 12px 7px 32px', borderRadius: 8,
                  border: '1.5px solid #e2e8f0', background: '#f8fafc',
                  color: '#111827', fontSize: 13, outline: 'none',
                  fontFamily: 'inherit', transition: 'border-color 0.2s',
                }}
              />
            </div>
            <select
              className="tv-fi"
              value={filterWay}
              onChange={e => setFilterWay(e.target.value)}
              style={{
                padding: '7px 10px', borderRadius: 8,
                border: '1.5px solid #e2e8f0', background: '#f8fafc',
                color: '#374151', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit',
              }}
            >
              {wayTypes.map(w => <option key={w} value={w}>{w === 'All' ? 'All Types' : w}</option>)}
            </select>
            <select
              className="tv-fi"
              value={filterStatus}
              onChange={e => setFilterStatus(e.target.value)}
              style={{
                padding: '7px 10px', borderRadius: 8,
                border: '1.5px solid #e2e8f0', background: '#f8fafc',
                color: '#374151', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit',
              }}
            >
              {statusTypes.map(s => <option key={s} value={s}>{s === 'All' ? 'All Statuses' : s}</option>)}
            </select>
          </div>
        </div>

        {/* Table */}
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 560 }}>
            <thead>
              <tr style={{ background: '#fafafa', borderBottom: '1px solid #f1f5f9' }}>
                {[
                  { label: 'Route',        hide: false },
                  { label: 'Trip Type',    hide: false },
                  { label: 'Travel Dates', hide: false },
                  { label: 'Days',         hide: false },
                  { label: 'Approver',     hide: true  },
                  { label: 'Applied On',   hide: true  },
                  { label: 'Status',       hide: false },
                ].map(({ label, hide }) => (
                  <th
                    key={label}
                    className={hide ? 'tv-hide-sm' : ''}
                    style={{
                      padding: '11px 16px', textAlign: 'left',
                      fontSize: 11, fontWeight: 700, color: '#94a3b8',
                      letterSpacing: '0.07em', textTransform: 'uppercase',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td
                    colSpan={8}
                    style={{ padding: 52, textAlign: 'center', color: '#94a3b8', fontSize: 14 }}
                  >
                    {travels.length === 0
                      ? '⏳ No travel requests found'
                      : '🔍 No records match your filters'}
                  </td>
                </tr>
              ) : filtered.map(t => {
                const completed   = isTravelCompleted(t.date_to)
                const cancellable = canCancel(t)
                // Dim cancelled rows visually
                const rowOpacity  = t.status === 'Cancelled' ? 0.6 : 1

                return (
                  <tr
                    key={t.id}
                    className="tv-row"
                    style={{ borderBottom: '1px solid #f1f5f9', background: '#fff', opacity: rowOpacity }}
                  >
                    {/* Route */}
                    <td style={{ padding: '14px 16px' }}>
                      <RouteCell from={t.from} to={t.to} />
                      {t.legs && t.legs.length > 1 && <LegsTooltip legs={t.legs} />}
                      {t.country && (
                        <div style={{ marginTop: 4, fontSize: 11, color: '#9ca3af' }}>
                          {t.country}
                        </div>
                      )}
                    </td>

                    {/* Trip type */}
                    <td style={{ padding: '14px 16px' }}>
                      {(() => {
                        const wc = wayColor[t.way] || wayColor['One Way']
                        return (
                          <span style={{
                            padding: '3px 9px', borderRadius: 6,
                            background: wc.bg, color: wc.color,
                            fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap',
                          }}>
                            {t.way}
                          </span>
                        )
                      })()}
                    </td>

                    {/* Dates */}
                    <td style={{ padding: '14px 16px', whiteSpace: 'nowrap' }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>
                        {fmt(t.date_from)}
                      </div>
                      {t.date_to && t.date_to !== t.date_from && (
                        <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>
                          → {fmt(t.date_to)}
                        </div>
                      )}
                      {/* "Trip ended" pill shown when date has passed and not cancelled */}
                      {completed && t.status !== 'Cancelled' && (
                        <span style={{
                          marginTop: 4, display: 'inline-block',
                          padding: '1px 7px', borderRadius: 99,
                          background: '#ecfdf5', color: '#065f46',
                          fontSize: 10, fontWeight: 700, border: '1px solid #6ee7b7',
                        }}>
                          Trip ended
                        </span>
                      )}
                    </td>

                    {/* Days */}
                    <td style={{ padding: '14px 16px' }}>
                      <span style={{
                        padding: '3px 10px', borderRadius: 99,
                        background: '#f1f5f9', color: '#374151',
                        fontSize: 12, fontWeight: 700,
                      }}>
                        {t.days}d
                      </span>
                    </td>

                    {/* Approver */}
                    <td className="tv-hide-sm" style={{ padding: '14px 16px' }}>
                      {t.approver ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                          <div style={{
                            width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
                            background: 'linear-gradient(135deg, #1d6fb8, #378add)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: 13, fontWeight: 800, color: '#fff',
                          }}>
                            {t.approver.name?.charAt(0) || '?'}
                          </div>
                          <span style={{ fontSize: 13, fontWeight: 600, color: '#111827', whiteSpace: 'nowrap' }}>
                            {t.approver.name}
                          </span>
                        </div>
                      ) : (
                        <span style={{ fontSize: 13, color: '#9ca3af' }}>—</span>
                      )}
                    </td>

                    {/* Applied on */}
                    <td className="tv-hide-sm" style={{ padding: '14px 16px', fontSize: 13, color: '#64748b' }}>
                      {fmt(t.apply_date)}
                    </td>

                    {/* Status */}
                    <td style={{ padding: '14px 16px' }}>
                      <StatusBadge status={t.status} />
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div style={{
          padding: '12px 24px', borderTop: '1px solid #f1f5f9',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          flexWrap: 'wrap', gap: 8,
        }}>
          <span style={{ fontSize: 13, color: '#94a3b8' }}>
            Showing {filtered.length} of {travels.length} requests
          </span>
          <div style={{ display: 'flex', gap: 6 }}>
            {['‹', '1', '›'].map((p, i) => (
              <button key={i} style={{
                padding: '5px 11px', borderRadius: 6,
                border: '1px solid #e2e8f0',
                background: p === '1' ? '#1d6fb8' : '#fff',
                color:      p === '1' ? '#fff' : '#64748b',
                cursor: 'pointer', fontSize: 13, fontFamily: 'inherit',
              }}>
                {p}
              </button>
            ))}
          </div>
        </div>
      </motion.div>

      {/* ── Cancel Confirmation Dialog ────────────────────────────────────── */}
      <AnimatePresence>
        {cancelTarget && (
          <CancelDialog
            travel={cancelTarget}
            onConfirm={handleCancelConfirm}
            onClose={() => setCancelTarget(null)}
          />
        )}
      </AnimatePresence>
    </div>
  )
}